import { Button } from "components/DesignSystem/Button/Button";
import { BareInput } from "components/DesignSystem/Input/Input";
import { KEY_ENTER, KEY_ESCAPE } from "constants/keyboardKeys";
import Quill, { Range } from "quill";
import { ChangeEvent, KeyboardEvent, useState } from "react";

type RichTextLinkInputProps = {
  quill: Quill;
  range: Range;
  onClose: () => void;
};

const USER_SOURCE = "user";
const LINK_FORMAT = "link";
const URL_SCHEME_REGEX = /^[a-z][a-z\d+.-]*:/i;
const DEFAULT_URL_SCHEME = "https://";

const withScheme = (url: string) =>
  URL_SCHEME_REGEX.test(url) ? url : `${DEFAULT_URL_SCHEME}${url}`;

export const RichTextLinkInput = ({
  quill,
  range,
  onClose,
}: RichTextLinkInputProps) => {
  const [initialLink] = useState(() => {
    const link = quill.getFormat(range)[LINK_FORMAT];
    return typeof link === "string" ? link : "";
  });
  const [url, setUrl] = useState(initialLink);
  const isEditingLink = Boolean(initialLink);

  const close = () => {
    onClose();
    quill.setSelection(range, USER_SOURCE);
  };

  // false removes the link
  const setLink = (href: string | false) => {
    close();

    if (href && range.length === 0) {
      quill.insertText(range.index, href, LINK_FORMAT, href, USER_SOURCE);
      return;
    }

    quill.formatText(range, LINK_FORMAT, href, USER_SOURCE);
  };

  const applyLink = () => {
    const trimmedUrl = url.trim();
    setLink(trimmedUrl ? withScheme(trimmedUrl) : false);
  };

  const removeLink = () => setLink(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    setUrl(event.target.value);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KEY_ENTER) {
      event.preventDefault();
      applyLink();
    }

    if (event.key === KEY_ESCAPE) {
      event.preventDefault();
      close();
    }
  };

  return (
    <div className="t-flex t-items-center t-gap-2 sm:t-flex-wrap">
      <BareInput
        autoFocus
        customSize="small"
        placeholder="Paste link here"
        aria-label="Link URL"
        value={url}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      <Button
        type="button"
        customType="primary"
        size="small"
        onClick={applyLink}
      >
        {isEditingLink ? "Update link" : "Add link"}
      </Button>
      {isEditingLink && (
        <Button
          type="button"
          customType="ghost"
          size="small"
          onClick={removeLink}
        >
          Remove
        </Button>
      )}
      <Button type="button" customType="ghost" size="small" onClick={close}>
        Cancel
      </Button>
    </div>
  );
};
